import React, { useState, useEffect } from "react";
import { FaPlus, FaMinus, FaEdit } from "react-icons/fa";
import SummaryApi from "../common";
import noimage from "../asset/noimage.png";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdditionalInformationStep = ({ courseId, courseTitle, isDarkMode }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [newFaqData, setNewFaqData] = useState({ question: "", answer: "" });
  const [showEditFaqModal, setShowEditFaqModal] = useState(false);
  const [currentEditingFaq, setCurrentEditingFaq] = useState(null);
  const [tools, setTools] = useState([]);
  const [showToolModal, setShowToolModal] = useState(false);
  const [toolImageFile, setToolImageFile] = useState(null);
  const [showEditToolModal, setShowEditToolModal] = useState(false);
  const [currentEditingTool, setCurrentEditingTool] = useState(null);
  const [editedToolImageFile, setEditedToolImageFile] = useState(null);

  useEffect(() => {
    if (courseId) {
      fetchFaqs();
      fetchTools();
    } else {
      setFaqs([]);
      setTools([]);
    }
  }, [courseId]);

  const fetchFaqs = async () => {
    try {
      const response = await fetch(SummaryApi.getFaqsByCourseID.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: courseId }),
      });
      const data = await response.json();
      if (response.ok && data.status) setFaqs(data.data);
      else setFaqs([]);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setFaqs([]);
    }
  };

  const fetchTools = async () => {
    try {
      const response = await fetch(SummaryApi.getToolsByCourseId.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: courseId }),
      });
      const data = await response.json();
      if (response.ok && data.status) setTools(data.data);
      else setTools([]);
    } catch (error) {
      console.error("Error fetching tools:", error);
      setTools([]);
    }
  };

  const handleSaveFaq = async () => {
    if (!courseId || !newFaqData.question.trim() || !newFaqData.answer.trim()) {
      toast.error("Please enter both a question and an answer.");
      return;
    }

    try {
      const res = await fetch(SummaryApi.createCourseFaqs.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: courseId, ...newFaqData }),
      });
      const data = await res.json();
      if (res.ok && data.status) {
        toast.success("FAQ created successfully!");
        fetchFaqs();
        setNewFaqData({ question: "", answer: "" });
        setShowFaqModal(false);
      } else {
        toast.error("Failed to create FAQ.");
      }
    } catch (error) {
      console.error("Error creating FAQ:", error);
      toast.error("An error occurred while creating FAQ.");
    }
  };

  const handleEditFaqClick = (faq) => {
    console.log("Editing FAQ:", faq);
    setCurrentEditingFaq({ ...faq });
    setShowEditFaqModal(true);
  };

  const handleEditToolClick = (tool) => {
    setCurrentEditingTool({ ...tool });
    setEditedToolImageFile(null);
    setShowEditToolModal(true);
  };

  const handleUpdateFaq = async () => {
    console.log("Attempting to update FAQ:", currentEditingFaq);
    if (!currentEditingFaq || !currentEditingFaq.information_id) {
      toast.error("No FAQ selected for editing.");
      return;
    }

    if (
      !currentEditingFaq.question.trim() ||
      !currentEditingFaq.answer.trim()
    ) {
      toast.error("Please enter both a question and an answer.");
      return;
    }

    try {
      const res = await fetch(SummaryApi.updateFaqs.url, {
        method: SummaryApi.updateFaqs.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          information_id: currentEditingFaq.information_id,
          course_id: currentEditingFaq.course_id,
          question: currentEditingFaq.question,
          answer: currentEditingFaq.answer,
          is_active: currentEditingFaq.is_active || 1,
        }),
      });
      const data = await res.json();
      if (res.ok && data.status) {
        toast.success("FAQ updated successfully!");
        fetchFaqs();
        setCurrentEditingFaq(null);
        setShowEditFaqModal(false);
      } else {
        toast.error(data.message || "Failed to update FAQ.");
      }
    } catch (error) {
      console.error("Error updating FAQ:", error);
      toast.error("An error occurred while updating FAQ.");
    }
  };

  const handleUpdateTool = async () => {
    if (!currentEditingTool) {
      toast.error("No tool selected for editing.");
      return;
    }

    const formData = new FormData();
    formData.append("tools_id", currentEditingTool.tools_id);
    formData.append("course_id", currentEditingTool.course_id);
    formData.append("is_active", currentEditingTool.is_active || 1);

    if (editedToolImageFile) {
      formData.append("tool_image", editedToolImageFile);
    }

    try {
      const res = await fetch(SummaryApi.updateTools.url, {
        method: SummaryApi.updateTools.method,
        body: formData,
      });

      const data = await res.json();
      if (res.ok && data.status) {
        toast.success("Tool updated successfully!");
        fetchTools();
        setCurrentEditingTool(null);
        setShowEditToolModal(false);
        setEditedToolImageFile(null);
      } else {
        toast.error(data.message || "Failed to update tool.");
      }
    } catch (error) {
      console.error("Error updating tool:", error);
      toast.error("An error occurred while updating tool.");
    }
  };

  const handleSaveTool = async () => {
    if (!courseId || !toolImageFile) {
      toast.error("Please select a tool image.");
      return;
    }

    const formData = new FormData();
    formData.append("course_id", courseId);
    formData.append("tool_image", toolImageFile);

    try {
      const res = await fetch(SummaryApi.createCourseTools.url, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.status) {
        toast.success("Tool added successfully!");
        setToolImageFile(null);
        fetchTools();
        setShowToolModal(false);
      } else {
        toast.error("Failed to add tool.");
      }
    } catch (error) {
      console.error("Error uploading tool:", error);
      toast.error("Upload failed.");
    }
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`p-6 mx-auto space-y-10 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'
    }`}>
      <ToastContainer />
      <h2 className="text-2xl font-semibold">Additional Information</h2>

      {/* FAQ Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">FAQ's</h3>
          <button
            onClick={() => setShowFaqModal(true)}
    className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
          >
            + Add New FAQ
          </button>
        </div>

        {faqs.length > 0 ? (
          faqs.map((faq, index) => (
            <div key={faq.information_id || index} className={`border rounded transition-colors duration-300 ${
              isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'
            }`}>
              <div className={`w-full flex justify-between items-center p-4 font-medium text-left transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex-grow text-left"
                >
                  {faq.question}
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleEditFaqClick(faq)}
                    className={`p-1 transition-colors duration-200 ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                    }`}
                    title="Edit FAQ"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => toggleFaq(index)}
                    className={`p-1 transition-colors duration-200 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    {openIndex === index ? <FaMinus /> : <FaPlus />}
                  </button>
                </div>
              </div>
              {openIndex === index && (
                <div className={`p-4 border-t text-sm transition-colors duration-300 ${
                  isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-300 text-gray-600'
                }`}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))
        ) : (
          <p className={`transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>No FAQs added yet.</p>
        )}
      </div>

      {/* TOOLS SECTION */}
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Tools</h3>
          <button
            onClick={() => setShowToolModal(true)}
className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
          >
            + Add Tool
          </button>
        </div>

        {tools.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tools.map((tool) => {
              const imageUrl = tool.tool_image
                ? `${SummaryApi.Images.url.replace("/api", "")}/${tool.tool_image}`
                : noimage;

              return (
                <div
                  key={tool.tools_id}
                  className={`border rounded p-2 shadow-sm relative transition-colors duration-300 ${
                    isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'
                  }`}
                >
                  <img
                    src={imageUrl}
                    alt="Tool"
                    className="w-full h-48 object-cover rounded"
                  />
                  <button
                    onClick={() => handleEditToolClick(tool)}
                    className={`absolute top-2 right-2 p-1 rounded transition-colors duration-200 ${
                      isDarkMode 
                        ? 'text-blue-400 hover:text-blue-300 bg-gray-700' 
                        : 'text-blue-600 hover:text-blue-800 bg-white'
                    }`}
                    title="Edit Tool"
                  >
                    <FaEdit />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className={`transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>No tools added yet.</p>
        )}
      </div>

      {/* ADD FAQ MODAL */}
      {showFaqModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-md transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Add New FAQ</h3>
            <input
              type="text"
              placeholder="Enter FAQ Question *"
              className={`w-full px-4 py-3 border rounded mb-4 focus:outline-none focus:ring-2 transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-600' 
                  : 'border-gray-300 focus:ring-purple-600'
              }`}
              value={newFaqData.question}
              onChange={(e) =>
                setNewFaqData({ ...newFaqData, question: e.target.value })
              }
            />
            <textarea
              rows="4"
              placeholder="Enter FAQ Answer *"
              className={`w-full px-4 py-3 border rounded mb-4 focus:outline-none focus:ring-2 transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-600' 
                  : 'border-gray-300 focus:ring-blue-600'
              }`}
              value={newFaqData.answer}
              onChange={(e) =>
                setNewFaqData({
                  ...newFaqData,
                  answer: e.target.value,
                })
              }
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowFaqModal(false);
                  setNewFaqData({ question: "", answer: "" });
                }}
                className={`px-4 py-3 rounded transition-colors duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFaq}
         className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
              >
                Add FAQ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT FAQ MODAL */}
      {showEditFaqModal && currentEditingFaq && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-md transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Edit FAQ</h3>
            <input
              type="text"
              placeholder="Enter FAQ Question *"
              className={`w-full px-4 py-3 border rounded mb-4 focus:outline-none focus:ring-2 transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-600' 
                  : 'border-gray-300 focus:ring-blue-600'
              }`}
              value={currentEditingFaq.question}
              onChange={(e) =>
                setCurrentEditingFaq({
                  ...currentEditingFaq,
                  question: e.target.value,
                })
              }
            />
            <textarea
              rows="4"
              placeholder="Enter FAQ Answer *"
              className={`w-full px-4 py-3 border rounded mb-4 focus:outline-none focus:ring-2 transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-600' 
                  : 'border-gray-300 focus:ring-blue-600'
              }`}
              value={currentEditingFaq.answer}
              onChange={(e) =>
                setCurrentEditingFaq({
                  ...currentEditingFaq,
                  answer: e.target.value,
                })
              }
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEditFaqModal(false);
                  setCurrentEditingFaq(null);
                }}
                className={`px-4 py-3 rounded transition-colors duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFaq}
                className="px-4 py-3 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors duration-200"
              >
                Update FAQ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD TOOL MODAL */}
      {showToolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-md transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Add Tool</h3>
            <input
              type="file"
              accept="image/*"
              className={`w-full mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
              onChange={(e) => setToolImageFile(e.target.files[0])}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowToolModal(false);
                  setToolImageFile(null);
                }}
                className={`px-4 py-3 rounded transition-colors duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTool}
               className={`px-4 py-3 text-white rounded flex items-center gap-2 disabled:opacity-70 transition-colors duration-300 ${
                  isDarkMode
                     ? 'bg-[#1C3664] hover:bg-blue-700 text-white'
              : 'bg-[#1C3664] text-white hover:bg-blue-800'
                }`}
              >
                Upload Tool
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT TOOL MODAL */}
      {showEditToolModal && currentEditingTool && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-full max-w-md transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>Edit Tool</h3>

            <img
              src={
                editedToolImageFile
                  ? URL.createObjectURL(editedToolImageFile)
                  : currentEditingTool.tool_image
                  ? `${SummaryApi.Images.url.replace("/api", "")}/${currentEditingTool.tool_image}`
                  : noimage
              }
              alt="Tool Preview"
              className="w-full h-48 object-cover rounded mb-4"
            />

            <input
              type="file"
              accept="image/*"
              className={`w-full mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
              onChange={(e) => setEditedToolImageFile(e.target.files[0])}
            />

            <label className={`flex items-center gap-2 mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <input
                type="checkbox"
                checked={currentEditingTool.is_active === 1}
                onChange={(e) =>
                  setCurrentEditingTool({
                    ...currentEditingTool,
                    is_active: e.target.checked ? 1 : 0,
                  })
                }
              />
              Active
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEditToolModal(false);
                  setCurrentEditingTool(null);
                  setEditedToolImageFile(null);
                }}
                className={`px-4 py-3 rounded transition-colors duration-200 ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTool}
                className="px-4 py-3 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors duration-200"
              >
                Update Tool
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalInformationStep;